import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Box,
  Flex,
  VStack,
  HStack,
  Text,
  Button,
  Divider,
  Spinner,
  useToast,
  Icon,
  Tooltip,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  useDisclosure,
} from '@chakra-ui/react';
import { FiRefreshCw, FiDownload, FiPrinter, FiClock, FiShield, FiAlertTriangle, FiCreditCard } from 'react-icons/fi';
import { getOrCreateToken, revokeToken } from 'services/qrService';
import { generateCarnetPng, getCarnetFilename, getSchoolInfo } from 'utils/carnetUtils';
import CarnetTemplate from './CarnetTemplate';

const QR_PREFIX = 'datofyqr://v1/';

const QRCard = ({ customer }) => {
  const [token, setToken]       = useState(null);
  const [loading, setLoading]   = useState(true);
  const [revoking, setRevoking]           = useState(false);
  const [error, setError]                 = useState(null);
  const [downloadingCarnet, setDownloadingCarnet] = useState(false);
  const [orientation, setOrientation]     = useState('horizontal');

  const { isOpen: isCarnetOpen, onOpen: onOpenCarnet, onClose: onCloseCarnet } = useDisclosure();
  const { isOpen: isRevokeOpen, onOpen: onOpenRevoke, onClose: onCloseRevoke } = useDisclosure();
  const qrRef = useRef(null);
  const toast = useToast();

  const wpUserId = customer?.id;
  const rut      = customer?.username;

  const fetchToken = useCallback(async () => {
    if (!wpUserId || !rut) return;
    setLoading(true);
    setError(null);
    try {
      const res = await getOrCreateToken(wpUserId, rut);
      setToken(res.data);
    } catch (e) {
      setError('No se pudo obtener el QR. Verifica la conexión con datofy-api.');
    } finally {
      setLoading(false);
    }
  }, [wpUserId, rut]);

  useEffect(() => {
    fetchToken();
  }, [fetchToken]);

  const handleRevoke = async () => {
    setRevoking(true);
    try {
      const res = await revokeToken(wpUserId, rut);
      setToken(res.data);
      toast({ title: 'QR regenerado', status: 'success', duration: 3000, isClosable: true });
    } catch {
      toast({ title: 'Error al regenerar QR', status: 'error', duration: 3000, isClosable: true });
    } finally {
      setRevoking(false);
      onCloseRevoke();
    }
  };

  const handleDownloadCarnet = async () => {
    if (!token) return;
    setDownloadingCarnet(true);
    try {
      const schoolInfo = getSchoolInfo();
      const qrValue = QR_PREFIX + token.token;
      const png = await generateCarnetPng(customer, qrValue, schoolInfo, orientation);
      const link = document.createElement('a');
      link.download = getCarnetFilename({ ...customer, username: customer?.username || rut });
      link.href = png;
      link.click();
    } catch (e) {
      toast({ title: 'Error al generar carnet', status: 'error', duration: 3000, isClosable: true });
    } finally {
      setDownloadingCarnet(false);
    }
  };

  const handleDownload = () => {
    const canvas = qrRef.current?.querySelector('canvas');
    if (!canvas) return;
    const link    = document.createElement('a');
    link.download = `qr-${rut}.png`;
    link.href     = canvas.toDataURL('image/png');
    link.click();
  };

  const handlePrint = () => {
    const canvas = qrRef.current?.querySelector('canvas');
    if (!canvas) return;
    const win    = window.open('', '_blank');
    const name   = `${customer?.first_name ?? ''} ${customer?.last_name ?? ''}`.trim();
    win.document.write(`
      <html><head><title>Carnet QR - ${name}</title>
      <style>
        body { font-family: sans-serif; text-align: center; padding: 40px; }
        .name { font-size: 24px; font-weight: bold; margin: 16px 0 4px; }
        .rut  { font-size: 14px; color: #666; margin-bottom: 20px; }
        img   { width: 280px; height: 280px; }
      </style></head><body>
      <div class="name">${name}</div>
      <div class="rut">RUT: ${rut}</div>
      <img src="${canvas.toDataURL('image/png')}" />
      </body></html>
    `);
    win.document.close();
    win.print();
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    return new Date(dateStr).toLocaleString('es-CL', {
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  };

  if (loading) {
    return (
      <Box
        bg="white" border="1px solid" borderColor="cream.line" borderRadius="20px"
        p={5} fontFamily="jakarta" textAlign="center"
      >
        <Spinner size="md" color="coral.500" />
        <Text mt={2} color="ink.400" fontSize="13px">Cargando carnet QR...</Text>
      </Box>
    );
  }

  if (error) {
    return (
      <Box bg="rose.bg" borderRadius="14px" p={4} fontFamily="jakarta">
        <Text fontSize="13.5px" color="rose.ink" fontWeight="600">{error}</Text>
      </Box>
    );
  }

  const qrValue = token ? QR_PREFIX + token.token : '';
  const studentName = `${customer?.first_name ?? ''} ${customer?.last_name ?? ''}`.trim();

  return (
    <Box fontFamily="jakarta">

      {/* ── Barra compacta de carnet ── */}
      <Flex
        bg="white" border="1px solid" borderColor="cream.line" borderRadius="20px"
        p={4} boxShadow="0 2px 12px -4px rgba(35,30,26,.08)"
        align="center" justify="space-between" flexWrap="wrap" gap={3}
      >
        <HStack spacing={3}>
          <Flex
            w="44px" h="44px" borderRadius="12px" bg="coral.soft"
            align="center" justify="center" flexShrink={0}
          >
            <Icon as={FiCreditCard} color="coral.700" boxSize="20px" />
          </Flex>
          <Box>
            <HStack spacing={2}>
              <Text fontWeight="800" fontSize="14.5px" color="ink.900">
                Carnet digital QR
              </Text>
              <Box as="span" px="8px" py="2px" borderRadius="999px" bg="mint.bg" color="mint.ink"
                fontSize="10.5px" fontWeight="700">
                Activo
              </Box>
            </HStack>
            <Text fontSize="12px" color="ink.400" fontWeight="500" mt="1px">
              Último escaneo: {formatDate(token?.last_used_at)}
            </Text>
          </Box>
        </HStack>
        <Button
          h="34px" px={4} borderRadius="10px" fontSize="12.5px" fontWeight="700"
          border="1.5px solid" borderColor="cream.line2" bg="white" color="ink.700"
          _hover={{ borderColor: 'coral.500', color: 'coral.500' }}
          onClick={onOpenCarnet}
        >
          Ver carnet
        </Button>
      </Flex>

      {/* ── Modal: carnet completo + acciones ── */}
      <Modal isOpen={isCarnetOpen} onClose={onCloseCarnet} isCentered size="2xl" scrollBehavior="inside">
        <ModalOverlay />
        <ModalContent mx={3} fontFamily="jakarta" borderRadius="18px">
          <ModalHeader fontSize="16px" fontWeight="800" color="ink.900">
            Carnet digital QR
            <Text fontSize="12px" color="ink.400" fontWeight="500" mt="2px">
              Identifica al alumno en los lectores del sistema. No contiene datos sensibles.
            </Text>
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <HStack spacing={8} align="flex-start" flexWrap="wrap">
              <VStack align="center" spacing={3} flexShrink={0}>
                <HStack spacing={1} bg="cream.bg" borderRadius="9px" p="3px">
                  <Button
                    size="sm" h="28px" px={3} borderRadius="7px" fontSize="12px" fontWeight="700"
                    bg={orientation === 'horizontal' ? 'white' : 'transparent'}
                    color={orientation === 'horizontal' ? 'ink.900' : 'ink.400'}
                    boxShadow={orientation === 'horizontal' ? '0 1px 3px rgba(0,0,0,0.12)' : 'none'}
                    _hover={{ color: 'ink.900' }}
                    onClick={() => setOrientation('horizontal')}
                  >
                    Horizontal
                  </Button>
                  <Button
                    size="sm" h="28px" px={3} borderRadius="7px" fontSize="12px" fontWeight="700"
                    bg={orientation === 'vertical' ? 'white' : 'transparent'}
                    color={orientation === 'vertical' ? 'ink.900' : 'ink.400'}
                    boxShadow={orientation === 'vertical' ? '0 1px 3px rgba(0,0,0,0.12)' : 'none'}
                    _hover={{ color: 'ink.900' }}
                    onClick={() => setOrientation('vertical')}
                  >
                    Vertical
                  </Button>
                </HStack>
                <Box ref={qrRef}>
                  <CarnetTemplate customer={customer} qrValue={qrValue} orientation={orientation} schoolInfo={getSchoolInfo()} />
                </Box>
              </VStack>

              <VStack align="flex-start" spacing={4} flex={1} minW="220px">
                <VStack align="flex-start" spacing={1} w="100%">
                  <HStack>
                    <Icon as={FiShield} color="coral.500" boxSize={4} />
                    <Text fontSize="12.5px" color="ink.400" fontWeight="600">Token ID</Text>
                  </HStack>
                  <Text fontSize="11px" fontFamily="mono" color="ink.700" wordBreak="break-all">
                    {token?.token}
                  </Text>
                </VStack>

                <Divider borderColor="cream.line" />

                <HStack spacing={8} flexWrap="wrap">
                  <VStack align="flex-start" spacing={1}>
                    <HStack>
                      <Icon as={FiClock} color="coral.500" boxSize={4} />
                      <Text fontSize="12.5px" color="ink.400" fontWeight="600">Último escaneo</Text>
                    </HStack>
                    <Text fontSize="13.5px" color="ink.900" fontWeight="700">
                      {formatDate(token?.last_used_at)}
                    </Text>
                  </VStack>
                  <VStack align="flex-start" spacing={1}>
                    <HStack>
                      <Icon as={FiClock} color="ink.400" boxSize={4} />
                      <Text fontSize="12.5px" color="ink.400" fontWeight="600">Generado el</Text>
                    </HStack>
                    <Text fontSize="13.5px" color="ink.700" fontWeight="600">
                      {formatDate(token?.generated_at)}
                    </Text>
                  </VStack>
                </HStack>

                <Divider borderColor="cream.line" />

                <VStack align="flex-start" spacing={2} w="100%">
                  <Text fontSize="11px" fontWeight="700" color="ink.400" textTransform="uppercase" letterSpacing="0.4px">
                    Acciones
                  </Text>
                  <HStack spacing={2} flexWrap="wrap">
                    <Button
                      h="34px" px={4} borderRadius="10px"
                      bg="coral.500" color="#fff" fontWeight="700" fontSize="12.5px"
                      _hover={{ bg: 'coral.600' }}
                      leftIcon={<FiCreditCard />}
                      onClick={handleDownloadCarnet}
                      isLoading={downloadingCarnet}
                      loadingText="Generando..."
                    >
                      Descargar carnet
                    </Button>
                    <Tooltip label="Imprimir carnet completo">
                      <Button
                        h="34px" px={4} borderRadius="10px" fontSize="12.5px" fontWeight="700"
                        border="1.5px solid" borderColor="cream.line2" bg="white" color="ink.700"
                        _hover={{ borderColor: 'coral.500', color: 'coral.500' }}
                        leftIcon={<FiPrinter />}
                        onClick={handlePrint}
                      >
                        Imprimir
                      </Button>
                    </Tooltip>
                    <Tooltip label="Descargar solo el QR">
                      <Button
                        variant="ghost" h="34px" px={2} fontSize="12.5px" color="ink.400" fontWeight="600"
                        leftIcon={<FiDownload />}
                        onClick={handleDownload}
                      >
                        Solo QR
                      </Button>
                    </Tooltip>
                  </HStack>
                </VStack>

                <Divider borderColor="cream.line" />

                <VStack align="flex-start" spacing={1} w="100%">
                  <Button
                    h="32px" px={3} borderRadius="9px" fontSize="12px" fontWeight="700"
                    border="1.5px solid" borderColor="cream.line2" bg="white" color="#D97706"
                    _hover={{ bg: 'butter.bg' }}
                    leftIcon={<FiRefreshCw />}
                    onClick={onOpenRevoke}
                    isLoading={revoking}
                  >
                    Regenerar QR
                  </Button>
                  <Text fontSize="11px" color="ink.400" fontWeight="500">
                    Invalida el QR actual de inmediato; el alumno necesitará uno nuevo.
                  </Text>
                </VStack>
              </VStack>
            </HStack>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" size="sm" onClick={onCloseCarnet}>Cerrar</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* ── Modal confirmación regenerar ── */}
      <Modal isOpen={isRevokeOpen} onClose={onCloseRevoke} isCentered size="sm">
        <ModalOverlay />
        <ModalContent mx={3} fontFamily="jakarta" borderRadius="18px">
          <ModalHeader fontSize="16px" fontWeight="800" color="ink.900">Regenerar QR</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <HStack mb={3}>
              <Icon as={FiAlertTriangle} color="#D97706" boxSize={5} />
              <Text fontWeight="700" fontSize="14px" color="ink.900">¿Estás seguro?</Text>
            </HStack>
            <Text fontSize="13px" color="ink.400" fontWeight="500">
              El QR actual de <strong>{studentName}</strong> quedará inválido de inmediato.
              Si el alumno tiene el carnet impreso o guardado, necesitará uno nuevo.
            </Text>
          </ModalBody>
          <ModalFooter gap={2}>
            <Button variant="ghost" size="sm" onClick={onCloseRevoke}>Cancelar</Button>
            <Button
              size="sm" borderRadius="9px" bg="#D97706" color="#fff" fontWeight="700"
              _hover={{ bg: '#B45309' }}
              onClick={handleRevoke} isLoading={revoking}
            >
              Sí, regenerar
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

    </Box>
  );
};

export default QRCard;
